import type { BuildingCategory, BuildingMetadata, BuildingStage } from '../../engine/world/types';

interface BuildingDefinition {
  readonly kind: string;
  readonly category: BuildingCategory;
  readonly footprint: { readonly width: number; readonly height: number };
  readonly production?: string;
}

export interface BuildingDoorAnchor {
  /** Horizontal offset from the building's bottom-centre render anchor. */
  readonly x: number;
  /** Point just behind the visible threshold, used while entering. */
  readonly insideY: number;
  /** Point immediately in front of the visible threshold. */
  readonly outsideY: number;
}

const DEFAULT_DOOR_ANCHOR: BuildingDoorAnchor = { x: 0, insideY: 0.5, outsideY: 0.84 };

/**
 * Door positions are tied to the visible voxel art rather than the logical
 * footprint. This keeps generated road spurs and character entrances aligned
 * with the painted threshold even when an asset is asymmetrical.
 */
const doorAnchors: Readonly<Record<string, BuildingDoorAnchor>> = {
  castle: { x: 0, insideY: 0.46, outsideY: 0.82 },
  warehouse: { x: -0.52, insideY: 0.48, outsideY: 0.86 },
  market: { x: 0, insideY: 0.42, outsideY: 0.82 },
  house: { x: -0.12, insideY: 0.48, outsideY: 0.84 },
  farm: { x: -0.18, insideY: 0.46, outsideY: 0.84 },
  woodcutter_camp: { x: 0, insideY: 0.42, outsideY: 0.8 },
  mine: { x: -0.08, insideY: 0.42, outsideY: 0.8 },
  quarry: { x: -0.08, insideY: 0.42, outsideY: 0.8 },
  iron_mine: { x: 0, insideY: 0.42, outsideY: 0.82 },
  clay_pit: { x: 0, insideY: 0.4, outsideY: 0.8 },
  smelter: { x: -0.12, insideY: 0.48, outsideY: 0.84 },
  mill: { x: -0.14, insideY: 0.48, outsideY: 0.86 },
  bakery: { x: -0.14, insideY: 0.48, outsideY: 0.84 },
  smithy: { x: -0.12, insideY: 0.48, outsideY: 0.84 },
  barracks: { x: 0, insideY: 0.48, outsideY: 0.84 },
  stable: { x: -0.1, insideY: 0.48, outsideY: 0.84 },
  watchtower: { x: 0, insideY: 0.48, outsideY: 0.82 },
  spy_office: { x: -0.18, insideY: 0.48, outsideY: 0.84 },
  brickyard: { x: 0.12, insideY: 0.46, outsideY: 0.84 },
  builder_yard: { x: 0.16, insideY: 0.46, outsideY: 0.84 },
  wall: { x: 0, insideY: 0.38, outsideY: 0.82 },
  siege_workshop: { x: 0, insideY: 0.48, outsideY: 0.86 },
  harbor: { x: 0, insideY: 0.46, outsideY: 0.82 },
  lighthouse: { x: 0, insideY: 0.46, outsideY: 0.82 },
  mage_tower: { x: 0, insideY: 0.46, outsideY: 0.82 },
};

const definitions: Record<string, BuildingDefinition> = {
  castle: { kind: 'castle', category: 'administration', footprint: { width: 3, height: 3 }, production: 'Prestige und Reichsverwaltung' },
  warehouse: { kind: 'warehouse', category: 'storage', footprint: { width: 2, height: 2 }, production: 'Lagerkapazität' },
  market: { kind: 'market', category: 'trade', footprint: { width: 2, height: 2 }, production: 'Gold und Handel' },
  house: { kind: 'house', category: 'housing', footprint: { width: 2, height: 2 }, production: 'Einwohnerplatz' },
  farm: { kind: 'farm', category: 'agriculture', footprint: { width: 3, height: 2 }, production: 'Nahrung' },
  woodcutter_camp: { kind: 'woodcutter_camp', category: 'production', footprint: { width: 2, height: 2 }, production: 'Holz' },
  mine: { kind: 'mine', category: 'production', footprint: { width: 2, height: 2 }, production: 'Stein und Erz' },
  quarry: { kind: 'quarry', category: 'production', footprint: { width: 2, height: 2 }, production: 'Stein' },
  iron_mine: { kind: 'iron_mine', category: 'production', footprint: { width: 3, height: 2 }, production: 'Eisen' },
  clay_pit: { kind: 'clay_pit', category: 'production', footprint: { width: 3, height: 2 }, production: 'Lehm' },
  smelter: { kind: 'smelter', category: 'production', footprint: { width: 2, height: 2 }, production: 'Metallbarren' },
  mill: { kind: 'mill', category: 'agriculture', footprint: { width: 2, height: 2 }, production: 'Mehl' },
  bakery: { kind: 'bakery', category: 'production', footprint: { width: 2, height: 2 }, production: 'Brot' },
  smithy: { kind: 'smithy', category: 'production', footprint: { width: 2, height: 2 }, production: 'Werkzeuge' },
  barracks: { kind: 'barracks', category: 'military', footprint: { width: 2, height: 2 }, production: 'Truppen' },
  watchtower: { kind: 'watchtower', category: 'military', footprint: { width: 1, height: 2 }, production: 'Verteidigung' },
  spy_office: { kind: 'spy_office', category: 'military', footprint: { width: 2, height: 2 }, production: 'Spionage' },
  brickyard: { kind: 'brickyard', category: 'production', footprint: { width: 3, height: 2 }, production: 'Ziegel' },
  builder_yard: { kind: 'builder_yard', category: 'production', footprint: { width: 3, height: 2 }, production: 'Baufortschritt' },
  wall: { kind: 'wall', category: 'military', footprint: { width: 4, height: 1 }, production: 'Verteidigung' },
  siege_workshop: { kind: 'siege_workshop', category: 'military', footprint: { width: 3, height: 2 }, production: 'Belagerungsgeräte' },
  stable: { kind: 'stable', category: 'military', footprint: { width: 2, height: 2 }, production: 'Karawanen und Patrouillen' },
  mage_tower: { kind: 'mage_tower', category: 'special', footprint: { width: 2, height: 2 }, production: 'Weltmagie' },
};

export function createBuildingMetadata(kind: string, stage: BuildingStage, variantId: string, assignedNpc?: string): BuildingMetadata {
  const definition = definitions[kind] ?? definitions.house;
  return {
    category: definition.category,
    stage,
    variantId,
    footprint: definition.footprint,
    production: definition.production,
    assignedNpc,
  };
}

export function getBuildingDoorAnchor(kind: string): BuildingDoorAnchor {
  return doorAnchors[kind] ?? DEFAULT_DOOR_ANCHOR;
}
